import { useMemo } from "react";
import { format } from "date-fns";

import { ArquivoDTO } from "@/types/arquivo.type";
import { ArquivoFiltrosCriteria, ArquivoFiltrosOptions, TipoArquivoMap } from "@workflow/types";

import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import {
  arquivosAdminQueries,
  arquivosAdminKeys,
} from "@/services/api/queries/arquivos-admin.queries";

import { Person } from "@/components/shared/person";
import { formatBytes } from "@/utils/format-bytes";
import { ArquivoDownloadButton, ArquivoNomeCell, TipoBadge } from "./arquivo-visual";

export function ArquivoListTable() {
  const { tableProps, isError, invalidate } = useServerDataTable<
    ArquivoDTO,
    ArquivoFiltrosOptions,
    ArquivoFiltrosCriteria
  >({
    queryOptions: arquivosAdminQueries.list,
    filters: arquivosAdminQueries.filtros,
    invalidateKey: arquivosAdminKeys.list,
  });

  const columns = useMemo<Column<ArquivoDTO>[]>(
    () => [
      {
        key: "actions",
        title: "Ações",
        width: "w-[80px]",
        render: (a) => (
          <div className="flex items-center justify-end">
            <ArquivoDownloadButton arquivoId={a.id} nome={a.nome} />
          </div>
        ),
      },
      {
        key: "arquivo.id",
        title: "ID",
        sortable: true,
        width: "min-w-[80px]",
        render: (a) => <span className="font-mono text-xs text-muted-foreground">{a.id}</span>,
        raw: (a) => a.id.toString(),
      },
      {
        key: "arquivo.nome",
        title: "Arquivo",
        sortable: true,
        width: "w-[280px] max-w-[280px]",
        render: (a) => <ArquivoNomeCell nome={a.nome} extensao={a.extensao} />,
        raw: (a) => a.nome,
      },
      {
        key: "arquivo.tipo",
        title: "Tipo",
        sortable: true,
        width: "min-w-[160px]",
        render: (a) => <TipoBadge tipo={a.tipo} />,
        raw: (a) => TipoArquivoMap[a.tipo] || a.tipo,
      },
      {
        key: "arquivo.tamanho",
        title: "Tamanho",
        sortable: true,
        width: "min-w-[110px]",
        render: (a) => (
          <span className="text-xs tabular-nums text-muted-foreground">
            {formatBytes(a.tamanho)}
          </span>
        ),
        raw: (a) => (a.tamanho ?? 0).toString(),
      },
      {
        key: "criadoPor",
        title: "Enviado por",
        width: "min-w-[220px]",
        render: (a) =>
          a.criadoPor ? (
            <Person.Root pessoa={a.criadoPor} asClickable className="flex items-center gap-2">
              <Person.Avatar />
              <Person.Name className="text-sm" />
            </Person.Root>
          ) : (
            <span className="text-xs italic text-muted-foreground/70">—</span>
          ),
        raw: (a) => a.criadoPor?.nome || "",
      },
      {
        key: "arquivo.criadoEm",
        title: "Enviado em",
        sortable: true,
        width: "min-w-[150px]",
        render: (a) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {a.criadoEm ? format(new Date(a.criadoEm), "dd/MM/yyyy HH:mm") : "—"}
          </span>
        ),
        raw: (a) => (a.criadoEm ? new Date(a.criadoEm).toISOString() : ""),
      },
    ],
    [],
  );

  return (
    <DataTable
      {...tableProps}
      storageKey="arquivos-admin-v1"
      columns={columns}
      defaultSortColumn="arquivo.id"
      defaultSortOrder="DESC"
      searchPlaceholder="Pesquisar por nome ou ID do arquivo..."
      emptyMessage={
        isError
          ? "Não foi possível carregar os arquivos. Tente recarregar."
          : "Nenhum arquivo encontrado."
      }
      isError={isError}
      onRetry={invalidate}
      exportFilename="arquivos"
    />
  );
}
